import type { TwitterBubble, MediaItem } from '@/stores/interfaces'
import { useRuntimeConfig } from '#app'

export function useMediaItems() {
  const {
    public: { dataStore },
  } = useRuntimeConfig()

  // Helper function to build media URLs
  function buildMediaUrl(path: string, isOffline: boolean = false): string {
    return isOffline ? `${dataStore}${path}` : path
  }

  // Get author profile picture
  function getAuthorPicSrc(bubble: TwitterBubble): string | undefined {
    if (bubble.offline && bubble.offlineMediaResources?.profile_pic) {
      return buildMediaUrl(bubble.offlineMediaResources.profile_pic as string, true)
    }
    return bubble.authorPic
  }

  // Get quote author profile picture
  function getQuoteAuthorPicSrc(bubble: TwitterBubble): string | undefined {
    if (bubble.offline && bubble.offlineMediaResources?.quote_author_pic) {
      const picPath = bubble.offlineMediaResources.quote_author_pic as string
      return buildMediaUrl(picPath, true)
    }
    const quotePic = bubble.quote?.quote_author_pic
    return quotePic ? (quotePic as string) : undefined
  }

  // Process root media resources
  function getRootMediaItems(bubble: TwitterBubble): MediaItem[] {
    const items: MediaItem[] = []
    const mediaResources =
      bubble.offline && bubble.sourceId
        ? (bubble.offlineMediaResources?.[bubble.sourceId] as Record<string, string> | undefined)
        : bubble.rootMediaResources

    if (!mediaResources) return items

    for (let i = 1; i <= 4; i++) {
      const videoKey = `video_${i}`
      const coverPhotoKey = `video_cover_photo_${i}`
      const imageKey = `image_${i}`
      const gifKey = `gif_${i}`
      const gifCoverPhotoKey = `gif_cover_photo_${i}`

      if (mediaResources[gifKey]) {
        const gifSrc = mediaResources[gifKey]
        const coverPhoto = mediaResources[gifCoverPhotoKey]
        items.push({
          type: 'gif',
          src: bubble.offline ? buildMediaUrl(gifSrc, true) : gifSrc,
          cover_photo: bubble.offline && coverPhoto ? buildMediaUrl(coverPhoto, true) : coverPhoto,
        })
      } else if (mediaResources[videoKey]) {
        const videoSrc = mediaResources[videoKey]
        const coverPhoto = mediaResources[coverPhotoKey]
        items.push({
          type: 'video',
          src: bubble.offline ? buildMediaUrl(videoSrc, true) : videoSrc,
          cover_photo: bubble.offline && coverPhoto ? buildMediaUrl(coverPhoto, true) : coverPhoto,
        })
      } else if (mediaResources[imageKey]) {
        const imageSrc = mediaResources[imageKey]
        items.push({
          type: 'image',
          src: bubble.offline ? buildMediaUrl(imageSrc, true) : imageSrc,
        })
      }
    }

    return items
  }

  // Process quote media resources
  function getQuoteMediaItems(bubble: TwitterBubble): MediaItem[] {
    const items: MediaItem[] = []

    if (bubble.offline && bubble.offlineMediaResources?.quote) {
      // Handle offline quote media
      const quoteMedia = bubble.offlineMediaResources.quote as Record<string, string>
      if (quoteMedia.video_1) {
        items.push({
          type: 'video',
          src: buildMediaUrl(quoteMedia.video_1, true),
          cover_photo: quoteMedia.video_cover_photo_1
            ? buildMediaUrl(quoteMedia.video_cover_photo_1, true)
            : undefined,
        })
      }
      // Handle other potential quote media types (images)
      for (let i = 1; i <= 4; i++) {
        const imageKey = `image_${i}`
        if (quoteMedia[imageKey]) {
          items.push({
            type: 'image',
            src: buildMediaUrl(quoteMedia[imageKey], true),
          })
        }
      }
    } else if (bubble.quote?.quote_media_resources) {
      // Handle online quote media
      const quoteMedia = bubble.quote.quote_media_resources as Record<string, string>
      if (quoteMedia.video_1) {
        items.push({
          type: 'video',
          src: quoteMedia.video_1,
          cover_photo: quoteMedia.video_cover_photo_1,
        })
      }
      // Handle other potential quote media types (images)
      for (let i = 1; i <= 4; i++) {
        const imageKey = `image_${i}`
        if (quoteMedia[imageKey]) {
          items.push({
            type: 'image',
            src: quoteMedia[imageKey],
          })
        }
      }
    }

    return items
  }

  // Process thread media resources
  function getThreadMediaItems(bubble: TwitterBubble): MediaItem[] {
    const items: MediaItem[] = []

    if (!bubble.rootMediaResources) return items

    for (let i = 1; i <= 4; i++) {
      const videoKey = `video_${i}`
      const coverPhotoKey = `video_cover_photo_${i}`
      const imageKey = `image_${i}`

      if (bubble.rootMediaResources[videoKey]) {
        const videoSrc = bubble.rootMediaResources[videoKey]
        const coverPhoto = bubble.rootMediaResources[coverPhotoKey]
        items.push({
          type: 'video',
          src: bubble.offline ? buildMediaUrl(videoSrc, true) : videoSrc,
          cover_photo: bubble.offline && coverPhoto ? buildMediaUrl(coverPhoto, true) : coverPhoto,
        })
      } else if (bubble.rootMediaResources[imageKey]) {
        const imageSrc = bubble.rootMediaResources[imageKey]
        items.push({
          type: 'image',
          src: bubble.offline ? buildMediaUrl(imageSrc, true) : imageSrc,
        })
      }
    }

    return items
  }

  // Get all media items for a tweet (for card view)
  function getAllMediaItems(bubble: TwitterBubble): MediaItem[] {
    const rootItems = getRootMediaItems(bubble)
    if (rootItems.length > 0) return rootItems

    const quoteItems = getQuoteMediaItems(bubble)
    if (quoteItems.length > 0) return quoteItems

    return []
  }

  // Helper to check if media exists
  function hasMedia(items: MediaItem[]): boolean {
    return items.length > 0
  }

  // Add this function alongside the other exports
  function getMediaItems(bubble: TwitterBubble): MediaItem[] {
    // First try root media
    const rootItems = getRootMediaItems(bubble)
    if (rootItems.length > 0) return rootItems

    // Then try quote media
    const quoteItems = getQuoteMediaItems(bubble)
    if (quoteItems.length > 0) return quoteItems

    return []
  }

  return {
    getAuthorPicSrc,
    getQuoteAuthorPicSrc,
    getRootMediaItems,
    getQuoteMediaItems,
    getThreadMediaItems,
    getMediaItems,
    getAllMediaItems,
    hasMedia,
    buildMediaUrl,
  }
}
